/**
 * Columns Admin Dialog — revamped for Task #289
 * Shows core/locked columns as static rows at top.
 * Custom columns always show editable input fields (no Edit-toggle pattern).
 * Drag-to-reorder works only on custom columns; locked core columns stay pinned.
 */
import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Loader2, Plus, Trash2, GripVertical, Lock } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

const CORE_COLUMNS_LOCKED = [
  { key: "date", label: "Date" },
  { key: "day", label: "Day" },
  { key: "status", label: "Status" },
  { key: "city", label: "City" },
  { key: "state", label: "State" },
  { key: "notes", label: "Notes" },
  { key: "driveTime", label: "Drive Time" },
  { key: "invoiceTotal", label: "Total Booking $" },
  { key: "region", label: "Region" },
];

interface WorkspaceColumn {
  id: string;
  key: string;
  label: string;
  dataType: string;
  sortOrder: number;
  isVisible: boolean;
}

interface ColumnsAdminDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ColumnsAdminDialog({ open, onOpenChange }: ColumnsAdminDialogProps) {
  const [newLabel, setNewLabel] = useState("");
  const [localOrder, setLocalOrder] = useState<WorkspaceColumn[]>([]);
  const [pendingLabels, setPendingLabels] = useState<Record<string, string>>({});
  const dragIndex = useRef<number | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: columns = [], isLoading } = useQuery<WorkspaceColumn[]>({
    queryKey: ["/api/schedule-columns"],
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["/api/schedule-columns"] });

  const sorted = localOrder.length > 0
    ? localOrder
    : [...columns].sort((a, b) => a.sortOrder - b.sortOrder);

  const createMutation = useMutation({
    mutationFn: async (label: string) => {
      const key = label.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
      const resp = await apiRequest("POST", "/api/schedule-columns", {
        key: key || `col_${Date.now()}`,
        label,
        dataType: "text",
        sortOrder: columns.length,
      });
      return resp.json();
    },
    onSuccess: () => { invalidate(); setNewLabel(""); setLocalOrder([]); toast({ title: "Column added" }); },
    onError: () => toast({ title: "Error", description: "Failed to add column.", variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, label, isVisible, sortOrder }: { id: string; label?: string; isVisible?: boolean; sortOrder?: number }) => {
      const body: Record<string, any> = {};
      if (label !== undefined) body.label = label;
      if (isVisible !== undefined) body.isVisible = isVisible;
      if (sortOrder !== undefined) body.sortOrder = sortOrder;
      const resp = await apiRequest("PATCH", `/api/schedule-columns/${id}`, body);
      return resp.json();
    },
    onSuccess: () => { invalidate(); },
    onError: () => toast({ title: "Error", description: "Failed to update column.", variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/schedule-columns/${id}`, {});
    },
    onSuccess: () => { invalidate(); setLocalOrder([]); toast({ title: "Column deleted" }); },
    onError: () => toast({ title: "Error", description: "Failed to delete column.", variant: "destructive" }),
  });

  const handleAdd = () => {
    if (!newLabel.trim()) return;
    createMutation.mutate(newLabel.trim());
  };

  const handleLabelChange = (id: string, newVal: string) => {
    setPendingLabels(prev => ({ ...prev, [id]: newVal }));
  };

  const handleLabelBlur = (col: WorkspaceColumn) => {
    const pending = pendingLabels[col.id];
    if (pending !== undefined && pending.trim() && pending.trim() !== col.label) {
      updateMutation.mutate({ id: col.id, label: pending.trim() });
      toast({ title: "Column renamed" });
    }
    setPendingLabels(prev => { const n = { ...prev }; delete n[col.id]; return n; });
  };

  // ── Drag-to-reorder (custom columns only) ────────────────────────────────
  const handleDragStart = (index: number) => {
    dragIndex.current = index;
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (dragIndex.current === null || dragIndex.current === index) return;
    const reordered = [...sorted];
    const [moved] = reordered.splice(dragIndex.current, 1);
    reordered.splice(index, 0, moved);
    dragIndex.current = index;
    setLocalOrder(reordered);
  };

  const handleDrop = () => {
    if (!localOrder.length) return;
    for (let i = 0; i < localOrder.length; i++) {
      if (localOrder[i].sortOrder !== i) {
        updateMutation.mutate({ id: localOrder[i].id, sortOrder: i });
      }
    }
    setLocalOrder(localOrder.map((c, i) => ({ ...c, sortOrder: i })));
    dragIndex.current = null;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Manage Columns</DialogTitle>
          <DialogDescription>
            Core columns are always present and locked. Add or rename custom columns below. Drag custom rows to reorder.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-1 max-h-[420px] overflow-y-auto pr-1">
            {/* Core locked columns — static rows */}
            <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide px-1 pt-1">
              Core columns (locked)
            </p>
            {CORE_COLUMNS_LOCKED.map((col) => (
              <div
                key={col.key}
                className="flex items-center gap-2 p-2 rounded-md border border-border bg-muted/40"
                data-testid={`core-column-row-${col.key}`}
              >
                <Lock className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <span className="flex-1 text-sm text-muted-foreground select-none">{col.label}</span>
                <span className="text-[10px] text-muted-foreground/60 font-mono">core</span>
              </div>
            ))}

            {/* Custom columns */}
            {sorted.length > 0 && (
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide px-1 pt-2">
                Custom columns
              </p>
            )}
            {sorted.map((col, index) => (
              <div
                key={col.id}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDrop={handleDrop}
                className="flex items-center gap-2 p-2 rounded-md border border-border bg-card hover:bg-muted/30 group cursor-grab active:cursor-grabbing"
                data-testid={`column-row-${col.id}`}
              >
                <GripVertical className="w-4 h-4 text-muted-foreground shrink-0" />

                <Input
                  value={pendingLabels[col.id] ?? col.label}
                  onChange={(e) => handleLabelChange(col.id, e.target.value)}
                  onBlur={() => handleLabelBlur(col)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                    if (e.key === "Escape") {
                      setPendingLabels(prev => { const n = { ...prev }; delete n[col.id]; return n; });
                      (e.target as HTMLInputElement).blur();
                    }
                  }}
                  className="h-7 text-sm flex-1"
                  data-testid={`input-column-label-${col.id}`}
                />

                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1">
                    <Switch
                      id={`vis-${col.id}`}
                      checked={col.isVisible}
                      onCheckedChange={(checked) => updateMutation.mutate({ id: col.id, isVisible: checked })}
                      data-testid={`toggle-column-visible-${col.id}`}
                    />
                    <Label htmlFor={`vis-${col.id}`} className="text-xs text-muted-foreground">Visible</Label>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 px-1 text-destructive hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => deleteMutation.mutate(col.id)}
                    disabled={deleteMutation.isPending}
                    data-testid={`button-delete-column-${col.id}`}
                  >
                    {deleteMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                  </Button>
                </div>
              </div>
            ))}

            {sorted.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-2">No custom columns yet. Add one below.</p>
            )}
          </div>
        )}

        <div className="flex gap-2 pt-2 border-t border-border">
          <Input
            placeholder="New column name (e.g. Option B)..."
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleAdd(); }}
            className="flex-1 h-8 text-sm"
            data-testid="input-new-column"
          />
          <Button
            size="sm"
            onClick={handleAdd}
            disabled={!newLabel.trim() || createMutation.isPending}
            className="h-8"
            data-testid="button-add-column"
          >
            {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
